import type {
  DiscountCategoryOption,
  DiscountCategoryOptionId,
  DiscountFormContext,
  DiscountTypeOption,
  PatientDefaultDiscount,
} from 'features/Procedure/state/procedure-api.type'

const PERCENT_PATTERN = /(\d+(?:\.\d+)?)\s*%/

export const DISCOUNT_CATEGORY = {
  PROCEDURES_ONLY: 'disc_cat_only_procedures',
  PRODUCTS_ONLY: 'disc_cat_only_products',
  PRODUCTS_AND_PROCEDURES: 'disc_cat_prod_proc',
} as const satisfies Record<string, DiscountCategoryOptionId>

const CONTEXT_TO_ALLOWED_CATEGORIES: Record<
  DiscountFormContext,
  ReadonlyArray<DiscountCategoryOptionId>
> = {
  treatments: [DISCOUNT_CATEGORY.PROCEDURES_ONLY, DISCOUNT_CATEGORY.PRODUCTS_AND_PROCEDURES],
  products: [DISCOUNT_CATEGORY.PRODUCTS_AND_PROCEDURES, DISCOUNT_CATEGORY.PRODUCTS_ONLY],
}

export const isCategoryAllowedForContext = (
  categoryId: DiscountCategoryOptionId | string | null | undefined,
  formContext: DiscountFormContext,
): boolean => {
  if (!categoryId) return false
  return CONTEXT_TO_ALLOWED_CATEGORIES[formContext].includes(categoryId as DiscountCategoryOptionId)
}

export const parseDiscountPercent = (
  discountType: DiscountTypeOption | null | undefined,
): number => {
  if (!discountType) return 0
  const fromNotes = discountType.notes?.match(PERCENT_PATTERN)
  if (fromNotes) return Number(fromNotes[1])
  const fromTitle = discountType.title?.match(PERCENT_PATTERN)
  if (fromTitle) return Number(fromTitle[1])
  return 0
}

export const computeDiscountAmount = (
  price: number,
  discountType: DiscountTypeOption | null | undefined,
): number => {
  if (!price || !discountType) return 0
  const percent = parseDiscountPercent(discountType)
  if (!percent) return 0
  const raw = (price * percent) / 100
  return Math.round(raw * 100) / 100
}

export const getAllowedDiscountCategories = (
  categories: ReadonlyArray<DiscountCategoryOption> | undefined,
  formContext: DiscountFormContext,
): DiscountCategoryOption[] => {
  if (!categories) return []
  return CONTEXT_TO_ALLOWED_CATEGORIES[formContext]
    .map((id) => categories.find((c) => c.option_id === id))
    .filter((c): c is DiscountCategoryOption => Boolean(c))
}

export const isDiscountDisabledForForm = (
  patientDefault: PatientDefaultDiscount | null | undefined,
  formContext: DiscountFormContext,
): boolean => {
  const categoryId = patientDefault?.discount_category?.option_id
  if (!categoryId) return false
  return !isCategoryAllowedForContext(categoryId, formContext)
}

export const getDefaultDiscountTypeIdForContext = (
  patientDefault: PatientDefaultDiscount | null | undefined,
  formContext: DiscountFormContext,
): string | null => {
  const categoryId = patientDefault?.discount_category?.option_id
  const typeId = patientDefault?.discount_type?.option_id
  if (!categoryId || !typeId) return null
  if (!isCategoryAllowedForContext(categoryId, formContext)) return null
  return typeId
}

export const findDiscountTypeById = (
  discountTypes: ReadonlyArray<DiscountTypeOption> | undefined,
  optionId: string | null | undefined,
): DiscountTypeOption | null => {
  if (!discountTypes || !optionId) return null
  return discountTypes.find((d) => d.option_id === optionId) ?? null
}
